// Knowledge base search page with enhanced filtering - 195 lines ✅
// @requirement SPEC-KB-SEARCH-001/FR-07: Filter by module
// @requirement SPEC-KB-SEARCH-001/FR-08: Filter by resolution status
// @requirement SPEC-KB-SEARCH-001/FR-09: Export search results

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { KBSearchBar } from '@/components/kb/KBSearchBar'
import { KBResultCard } from '@/components/kb/KBResultCard'
import { SearchFiltersPanel } from '@/components/kb/SearchFilters'
import { ExportResults } from '@/components/kb/ExportResults'
import { LoadingSpinner } from '@/components/layout/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Search, AlertTriangle, RefreshCw, BookOpen, Network } from 'lucide-react'
import { KBEntryCard } from '@/components/kb/KBEntryCard'
import { useKBSearch } from '@/hooks/useKBSearch'
import { useKnowledgeGraph, KGNode } from '@/hooks/useKnowledgeGraph'
import { KnowledgeGraphView } from '@/components/kg/KnowledgeGraphView'
import { NodeDetailPanel } from '@/components/kg/NodeDetailPanel'
import { supabase } from '@/lib/supabase'
import type { SearchFilters } from '@/types/knowledge'
import type { SearchResult } from '@/types/knowledge'

interface KBEntry {
  id: string
  title: string
  description: string | null
  content: string | null
  category: string
  source_type: string
  d365_versions: string[]
  d365_modules: string[]
  is_validated: boolean | null
  created_at: string
}

export function KnowledgeBase(): JSX.Element {
  const queryClient = useQueryClient()
  const { search: kbSearch, results: _searchResults, isSearching, error: _searchError, reset: _reset } = useKBSearch()
  const [results, setResults] = useState<SearchResult[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedNode, setSelectedNode] = useState<KGNode | null>(null)

  // Knowledge graph data
  const { nodes, edges, isLoading: kgLoading, error: kgError } = useKnowledgeGraph({
    limit: 100,
  })

  // Load all KB entries by default
  const allEntriesQuery = useQuery({
    queryKey: ['knowledge-entries-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('knowledge_entries')
        .select('id, title, description, content, category, source_type, source_id, d365_versions, d365_modules, is_validated, is_archived, created_at, organization_id')
        .eq('is_archived', false)
        .order('created_at', { ascending: false })
        .limit(50)
      console.log('[KB] Loaded entries:', data?.length, 'error:', error)
      if (error) throw error
      return (data ?? []) as KBEntry[]
    },
  })
  const handleDeleteEntry = async (entryId: string) => {
    if (!confirm('Delete this knowledge base entry?')) return
    await supabase.from('knowledge_entries').update({ is_archived: true } as never).eq('id', entryId)
    queryClient.invalidateQueries({ queryKey: ['knowledge-entries-all'] })
  }

  const [error, setError] = useState<Error | null>(null)
  const [lastQuery, setLastQuery] = useState<{ query: string; filters: SearchFilters } | null>(null)
  const [filters, setFilters] = useState<SearchFilters>({})
  const [showFilters, setShowFilters] = useState(false)
  const [showMatchExplanations, setShowMatchExplanations] = useState(true)
  const isLoading = isSearching

  const handleSearch = async (query: string, filters: SearchFilters): Promise<void> => {
    setHasSearched(true)
    setError(null)
    setLastQuery({ query, filters })

    try {
      const response = await kbSearch({
        query,
        d365Version: filters.d365Version,
        d365Module: filters.modules?.[0],
      })
      setResults(response.results)
    } catch (err) {
      setError(err as Error)
      console.error('Search failed:', err)
    }
  }

  const handleRetry = (): void => {
    if (lastQuery) {
      handleSearch(lastQuery.query, lastQuery.filters)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-kaycoo-teal-dark">Knowledge Base</h1>
        <p className="text-kaycoo-charcoal-2">
          Search our database of D365 F&O issues and solutions or explore the knowledge graph
        </p>
      </div>

      <Tabs defaultValue="search" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="search" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Search
          </TabsTrigger>
          <TabsTrigger value="graph" className="flex items-center gap-2">
            <Network className="h-4 w-4" />
            Graph View
          </TabsTrigger>
        </TabsList>

        {/* Search Tab */}
        <TabsContent value="search" className="space-y-6">
          <KBSearchBar onSearch={handleSearch} isLoading={isLoading} />

          {/* Advanced Filters */}
          <SearchFiltersPanel
            filters={filters}
            onFiltersChange={(newFilters) => {
              setFilters(newFilters)
              if (lastQuery) {
                handleSearch(lastQuery.query, newFilters)
              }
            }}
            showFilters={showFilters}
            onToggle={() => setShowFilters(!showFilters)}
          />

          {isLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner message="Searching knowledge base..." />
            </div>
          )}

          {error && !isLoading && (
            <Alert variant="destructive" className="border-kaycoo-burgundy-dark bg-kaycoo-burgundy-pale">
              <AlertTriangle className="h-4 w-4 text-kaycoo-burgundy-dark" />
              <AlertDescription className="flex items-center justify-between text-kaycoo-burgundy-dark">
                <span>Search failed: {error.message}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetry}
                  className="ml-4 border-kaycoo-burgundy text-kaycoo-burgundy-dark hover:bg-kaycoo-burgundy hover:text-white"
                >
                  <RefreshCw className="mr-1 h-3 w-3" />
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && !error && hasSearched && results.length === 0 && (
            <Alert className="border-kaycoo-blue bg-kaycoo-blue-pale">
              <Search className="h-4 w-4 text-kaycoo-blue-dark" />
              <AlertDescription className="text-kaycoo-blue-dark">
                No results found. Try adjusting your search query or filters.
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && results.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-kaycoo-charcoal-2">
                  Found {results.length} result{results.length !== 1 ? 's' : ''}
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowMatchExplanations(!showMatchExplanations)}
                    className="text-kaycoo-charcoal-2 hover:text-kaycoo-teal-dark"
                  >
                    {showMatchExplanations ? 'Hide' : 'Show'} match explanations
                  </Button>
                  <ExportResults
                    results={results}
                    query={lastQuery?.query || ''}
                  />
                </div>
              </div>
              <div className="space-y-4">
                {results.map((result) => (
                  <KBResultCard
                    key={result.issue.id}
                    result={result}
                    query={lastQuery?.query}
                    showMatchExplanation={showMatchExplanations}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Default listing — all KB entries when no search active */}
          {!hasSearched && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-kaycoo-teal-dark">
                  <BookOpen className="h-5 w-5" />
                  All Knowledge Base Entries ({allEntriesQuery.data?.length ?? 0})
                </h2>
              </div>
              {allEntriesQuery.isLoading && <LoadingSpinner message="Loading entries..." />}
              {allEntriesQuery.error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>Failed to load entries: {(allEntriesQuery.error as Error).message}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                {allEntriesQuery.data?.map((entry) => (
                  <KBEntryCard key={entry.id} entry={entry} onDelete={handleDeleteEntry} />
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Graph View Tab */}
        <TabsContent value="graph" className="space-y-6">
          {kgLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner message="Loading knowledge graph..." />
            </div>
          )}

          {kgError && !kgLoading && (
            <Alert variant="destructive" className="border-kaycoo-burgundy-dark bg-kaycoo-burgundy-pale">
              <AlertTriangle className="h-4 w-4 text-kaycoo-burgundy-dark" />
              <AlertDescription className="text-kaycoo-burgundy-dark">
                Failed to load knowledge graph: {(kgError as Error).message}
              </AlertDescription>
            </Alert>
          )}

          {!kgLoading && !kgError && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <KnowledgeGraphView
                  nodes={nodes}
                  edges={edges}
                  onNodeClick={setSelectedNode}
                />
              </div>
              <div className="lg:col-span-1">
                {selectedNode ? (
                  <NodeDetailPanel
                    node={selectedNode}
                    onClose={() => setSelectedNode(null)}
                  />
                ) : (
                  <div className="flex h-[600px] items-center justify-center rounded-lg border border-kaycoo-teal-dark bg-gray-50 p-8 text-center">
                    <div className="text-kaycoo-charcoal-2">
                      <Network className="mx-auto mb-4 h-12 w-12 opacity-50" />
                      <p className="text-sm">Click on a node to view details</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {!kgLoading && !kgError && nodes.length === 0 && (
            <Alert className="border-kaycoo-blue bg-kaycoo-blue-pale">
              <AlertTriangle className="h-4 w-4 text-kaycoo-blue-dark" />
              <AlertDescription className="text-kaycoo-blue-dark">
                No knowledge graph data available yet. The graph will populate as you add issues and knowledge entries.
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
